import { type Steps } from '../workflow/schemaTypes';
import { workflowStepMap } from '../workflow/schema';

/**
 * Escapes regex special characters in a string to prevent ReDoS attacks
 * @param str - The string to escape
 * @returns The escaped string safe for use in RegExp
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Converts a route template to a safe regex pattern
 * @param routeTemplate - Route template (e.g. '/$retailer/item-selection')
 * @param placeholder - The placeholder to replace (default: '$retailer')
 * @param pattern - The regex pattern to use as replacement (default: '[^/]+')
 * @returns Safe regex pattern string
 */
const routeToRegexPattern = (
  routeTemplate: string,
  placeholder: string = '$retailer',
  pattern: string = '[^/]+',
): string => {
  const escaped = escapeRegex(routeTemplate);
  const escapedPlaceholder = escapeRegex(placeholder);
  return escaped.replace(escapedPlaceholder, pattern);
};

/**
 * Checks if a given route matches any valid workflow step route
 * @param route - The route to check (e.g. '/$retailer/item-selection')
 * @returns boolean indicating if the route is a valid workflow step
 */
export const isValidWorkflowRoute = (route: string): boolean => {
  // Get all valid workflow routes
  const validRoutes = Object.values(workflowStepMap).map((step) => step.route);

  // Check if the route matches any valid workflow route pattern
  return validRoutes.some((validRoute) => {
    const routePattern = routeToRegexPattern(validRoute);
    const regex = new RegExp(`^${routePattern}$`);
    return regex.test(route);
  });
};

/**
 * Gets the workflow step ID from a route
 * @param route - The route to check (e.g. '/$retailer/item-selection')
 * @returns The step ID if valid, undefined if not a valid workflow route
 */
export const getStepIdFromRoute = (route: string): Steps | undefined => {
  const validRoutes = Object.entries(workflowStepMap);

  for (const [stepId, stepConfig] of validRoutes) {
    const routePattern = routeToRegexPattern(stepConfig.route);
    const regex = new RegExp(`^${routePattern}$`);
    if (regex.test(route)) {
      return stepId as Steps;
    }
  }

  return undefined;
};
